import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Clock, 
  Calendar, 
  Info, 
  ChevronDown, 
  ChevronUp, 
  Activity, 
  Search
} from 'lucide-react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import toast from 'react-hot-toast';

interface ImportLog {
  id: string;
  type: 'scraper' | 'manual';
  fileName: string;
  cruiseLines?: string[];
  status: 'success' | 'failed';
  startedAt: any;
  completedAt: any;
  newRecords: number;
  updatedRecords: number;
  newShipRecords?: number;
  updatedShipRecords?: number;
  errors: any[];
  informationalMessages?: any[];
  summary?: {
    totalFileRows: number;
    processedRows: number;
    skippedRows: number;
    errorRows: number;
    shipRows: number;
    skippedShipRows: number;
    errorShipRows: number;
    updatedFieldCounts?: {
      departurePort: number;
      itineraryTitle: number;
      cruiseLength: number;
      portNames: number;
      portDates: number;
      portTimes: number;
      portsCount: number;
    };
  };
}

const AdminImportLogs = () => {
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scraper' | 'manual' | 'failed'>('all');
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'itineraryImportLogs'),
        orderBy('startedAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      const results: ImportLog[] = snap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          type: data.type,
          fileName: data.fileName || 'Unknown File',
          cruiseLines: data.cruiseLines || [],
          status: data.status,
          startedAt: data.startedAt?.toDate ? data.startedAt.toDate() : new Date(data.startedAt),
          completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : new Date(data.completedAt),
          newRecords: data.newRecords || 0,
          updatedRecords: data.updatedRecords || 0,
          newShipRecords: data.newShipRecords || 0,
          updatedShipRecords: data.updatedShipRecords || 0,
          errors: data.errors || [],
          informationalMessages: data.informationalMessages || [],
          summary: data.summary
        };
      });
      setLogs(results);
    } catch (error) {
      console.error('Error fetching import logs:', error);
      toast.error('Failed to load import logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const formatDuration = (start: Date, end: Date) => {
    const diffMs = end.getTime() - start.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return `${diffSecs}s`;
    const mins = Math.floor(diffSecs / 60);
    const secs = diffSecs % 60;
    return `${mins}m ${secs}s`;
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.fileName.toLowerCase().includes(searchQuery.toLowerCase()) || 
      log.type.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'scraper') return log.type === 'scraper' && matchesSearch;
    if (filter === 'manual') return log.type === 'manual' && matchesSearch;
    if (filter === 'failed') return log.status === 'failed' && matchesSearch;
    return matchesSearch;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="h-8 w-8 text-fuchsia-600" />
            Import History & Webhook Logs
          </h1>
          <p className="text-gray-600 mt-1">Review scraper webhooks and manual itinerary upload logs</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex border-b border-gray-100 w-full sm:w-auto text-sm">
          {(['all', 'scraper', 'manual', 'failed'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`pb-2 px-3 font-semibold capitalize border-b-2 transition-colors ${
                filter === tab
                  ? 'border-fuchsia-600 text-fuchsia-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'all' && 'All Imports'}
              {tab === 'scraper' && 'Scraper Webhooks'}
              {tab === 'manual' && 'Manual Uploads'}
              {tab === 'failed' && 'Failed'}
            </button>
          ))}
        </div>

        <div className="relative rounded-md shadow-sm w-full sm:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by file or dataset ID..."
            className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-fuchsia-600 border-t-transparent"></div>
            <p className="text-gray-500 font-medium">Loading history logs...</p>
          </div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="p-16 text-center text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col items-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-2" />
          <p className="text-lg font-semibold text-gray-800">No logs found</p>
          <p className="text-sm text-gray-500 mt-1">There are no records matching your current filter.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map(log => {
            const isExpanded = expandedLogId === log.id;
            const duration = formatDuration(log.startedAt, log.completedAt);
            const hasErrors = log.errors.length > 0;
            const hasMessages = log.informationalMessages && log.informationalMessages.length > 0;

            return (
              <div 
                key={log.id} 
                className={`bg-white rounded-lg border shadow-sm overflow-hidden transition-all duration-200 ${
                  isExpanded ? 'border-indigo-200 ring-1 ring-indigo-500/20' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Log Header Row */}
                <div 
                  onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                  className="p-4 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 cursor-pointer select-none bg-gray-50/50 hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {log.status === 'success' ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
                    )}
                    
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold uppercase ${
                          log.type === 'scraper' ? 'bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
                        }`}>
                          {log.type}
                        </span>
                        <h4 className="font-bold text-gray-900 truncate max-w-md">{log.fileName}</h4>
                        {log.cruiseLines && log.cruiseLines.length > 0 && !log.fileName.includes('(') && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {log.cruiseLines.map(cl => (
                              <span key={cl} className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[11px] font-semibold">
                                {cl}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {log.startedAt.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Duration: {duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between md:justify-end gap-6">
                    {/* Stats summary badges */}
                    <div className="flex items-center gap-4 text-xs font-semibold">
                      <div className="text-center">
                        <span className="text-gray-500 block uppercase text-[10px]">New Itins</span>
                        <span className="text-indigo-600 block text-sm">{log.newRecords}</span>
                      </div>
                      <div className="text-center">
                        <span className="text-gray-500 block uppercase text-[10px]">Updated</span>
                        <span className="text-gray-700 block text-sm">{log.updatedRecords}</span>
                      </div>
                      {(log.newShipRecords !== undefined || log.updatedShipRecords !== undefined) && (
                        <div className="text-center">
                          <span className="text-gray-500 block uppercase text-[10px]">Ships (N/U)</span>
                          <span className="text-emerald-600 block text-sm">
                            {log.newShipRecords || 0} / {log.updatedShipRecords || 0}
                          </span>
                        </div>
                      )}
                      {hasErrors && (
                        <div className="text-center bg-red-50 text-red-700 px-2.5 py-1 rounded border border-red-100">
                          <span className="block text-[9px] uppercase font-bold">Errors</span>
                          <span className="block text-xs">{log.errors.length}</span>
                        </div>
                      )}
                    </div>

                    <div className="text-gray-400">
                      {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      className="border-t border-gray-100 overflow-hidden"
                    >
                      <div className="p-5 space-y-6 bg-white text-sm">
                        {/* Summary details */}
                        {log.summary && (
                          <div>
                            <h5 className="font-bold text-gray-800 uppercase text-xs mb-3">Sailing & Ship Processing Summary</h5>
                            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-4">
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Total File Rows</span>
                                <span className="text-lg font-bold text-gray-800 block mt-1">{log.summary.totalFileRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Processed Itins</span>
                                <span className="text-lg font-bold text-gray-800 block mt-1">{log.summary.processedRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Skipped Itins</span>
                                <span className="text-lg font-bold text-gray-800 block mt-1">{log.summary.skippedRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Itinerary Errors</span>
                                <span className="text-lg font-bold text-red-600 block mt-1">{log.summary.errorRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Total Ships</span>
                                <span className="text-lg font-bold text-gray-800 block mt-1">{log.summary.shipRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Skipped Ships</span>
                                <span className="text-lg font-bold text-gray-800 block mt-1">{log.summary.skippedShipRows}</span>
                              </div>
                              <div className="p-3 bg-gray-50 rounded border border-gray-100 text-center">
                                <span className="text-[10px] text-gray-500 uppercase block">Ship Errors</span>
                                <span className="text-lg font-bold text-red-600 block mt-1">{log.summary.errorShipRows}</span>
                              </div>
                            </div>

                            {log.summary.updatedFieldCounts && (
                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <h6 className="font-semibold text-gray-700 text-xs uppercase mb-2">Field Updates Breakdown</h6>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Port Names</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.portNames || 0}</span>
                                  </div>
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Port Times</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.portTimes || 0}</span>
                                  </div>
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Departure Port</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.departurePort || 0}</span>
                                  </div>
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Titles</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.itineraryTitle || 0}</span>
                                  </div>
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Port Dates</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.portDates || 0}</span>
                                  </div>
                                  <div className="p-2 bg-blue-50/50 rounded border border-blue-100 flex items-center justify-between">
                                    <span className="text-xs text-gray-600">Port Structure</span>
                                    <span className="text-xs font-bold text-blue-700">{log.summary.updatedFieldCounts.portsCount || 0}</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Errors List */}
                        {hasErrors && (
                          <div>
                            <h5 className="font-bold text-red-700 uppercase text-xs mb-2.5 flex items-center gap-1.5">
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                              Validation & Database Errors ({log.errors.length})
                            </h5>
                            <div className="max-h-60 overflow-y-auto rounded-lg border border-red-100 bg-red-50/10 divide-y divide-red-50">
                              {log.errors.map((err, errIdx) => (
                                <div key={errIdx} className="p-3 text-xs flex items-start gap-4">
                                  {err.row > 0 && (
                                    <span className="inline-flex items-center rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                                      Row {err.row}
                                    </span>
                                  )}
                                  <div className="flex-1">
                                    {err.field && <span className="font-semibold text-gray-700 mr-2">Field: [{err.field}]</span>}
                                    <span className="text-red-700">{err.message}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Informational / Log messages */}
                        {hasMessages && (
                          <div>
                            <h5 className="font-bold text-blue-700 uppercase text-xs mb-2.5 flex items-center gap-1.5">
                              <Info className="h-4 w-4 text-blue-500" />
                              Informational logs ({log.informationalMessages?.length})
                            </h5>
                            <div className="max-h-60 overflow-y-auto rounded-lg border border-blue-100 bg-blue-50/10 divide-y divide-blue-50 font-mono text-xs">
                              {log.informationalMessages?.map((msg, msgIdx) => {
                                const isWarning = msg.type === 'ship_case_corrected' || msg.type === 'ship_url_updated';
                                let text = '';
                                if (msg.type === 'ship_added') text = `Ship Added: "${msg.shipName}" to ${msg.cruiseLine}`;
                                else if (msg.type === 'ship_case_corrected') text = `Ship Case Corrected: "${msg.oldShipName}" → "${msg.shipName}"`;
                                else if (msg.type === 'ship_url_added') text = `Ship URL Added (${msg.urlType}): ${msg.url}`;
                                else if (msg.type === 'ship_url_updated') text = `Ship URL Updated (${msg.urlType}): ${msg.oldUrl} → ${msg.url}`;
                                else if (msg.type === 'ship_metadata_updated') text = `Ship Metadata Updated: "${msg.shipName}"`;
                                else text = `${msg.type}: ${JSON.stringify(msg)}`;

                                return (
                                  <div key={msgIdx} className="p-2.5 text-blue-800 flex items-center gap-1.5">
                                    {isWarning ? (
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                    ) : (
                                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                    )}
                                    <span>{text}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {!hasErrors && !hasMessages && (
                          <div className="p-4 bg-emerald-50/30 rounded-lg border border-emerald-100 text-emerald-800 flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                            <span>This import completed flawlessly without any warnings or validation errors.</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default AdminImportLogs;
